import {useEffect, useCallback, useState} from "react";
import {IStartDataCollectionStep, TProtocolId} from "../../../types/protocol";
import {IDirConfig} from "../../../types/protocolDefinitions";
import {useNeurotuneStore} from "@/common/store/neurotune.ts";
import {isTauriAvailable, isNeurotuneIPCVersionSupported} from "@/common/utils/tauri.ts";
import {showErrorNotification} from "@/common/notifications.tsx";
import {useUploadStatusStore} from "@/protocols/persistence/useUploadStatusStore";
import * as tauriService from "../../../../common/services/tauriService";
import {TSubjectId} from "@/common/types";
import {getPresetDefaults} from "./constants";

export interface FileAcquisitionConfig {
    extensions: string[]
    min_file_age: number
    dir_path?: string
    recursive?: boolean
    after_upload?: 'backup' | 'delete' | string
}

export const buildUploaderConfigs = (configs?: IDirConfig[], fallbackDir?: string): FileAcquisitionConfig[] => {
    if (!configs || configs.length === 0) {
        return [];
    }

    return configs.flatMap(config => {
        let extensions: string[];
        let minFileAge: number;

        if (config.uploader_preset && config.uploader_preset !== "custom") {
            const defaults = getPresetDefaults(config.uploader_preset);
            extensions = defaults.extensions;
            minFileAge = defaults.min_file_age;
        } else if (config.uploader_custom_extensions && config.uploader_custom_extensions.length > 0) {
            extensions = config.uploader_custom_extensions.map((extConfig) => extConfig.extension)
            // Custom rows are now emitted as a single source; keep the first
            // extension's age gate as the config-level min age.
            // UI only supports one min age per row, so this is consistent with the UI.
            // It is not currently possible to set different min_file_age for different extensions in the same row.
            minFileAge = config.uploader_custom_extensions[0].min_file_age
        } else {
            // no valid configurations
            return [];
        }

        return [{
            extensions,
            min_file_age: minFileAge,
            dir_path: config.dir_path || fallbackDir,
            recursive: config.recursive,
            after_upload: config.after_upload,
        }];
    });
};

export interface DataCollectionProps {
    step: IStartDataCollectionStep;
    subjectId: TSubjectId;
    /**
     * When false, the hook will not perform any side effects (polling, API calls).
     * Used to allow the hook to be called unconditionally while respecting React's Rules of Hooks.
     */
    enabled?: boolean;
}

/**
 * Shared function to check Tauri availability and version support.
 * Returns a promise that resolves to true if Tauri is available and supported.
 */
export const checkTauriAvailability = async (): Promise<boolean> => {
    if (isTauriAvailable()) {
        const versionSupported = await isNeurotuneIPCVersionSupported();
        return !!versionSupported;
    }
    return false;
};

/**
 * Hook to manage Tauri availability state.
 * Checks availability on mount and provides the current state.
 */
export const useTauriAvailability = (enabled: boolean = true) => {
    const [isTauri, setIsTauri] = useState(false);

    useEffect(() => {
        if (enabled) {
            checkTauriAvailability().then((available) => {
                setIsTauri(available);
            });
        }
    }, [enabled]);

    return isTauri;
};

/**
 * Hook to manage stopping data collection.
 * Drives the Rust upload queue shutdown (which also drops the
 * watcher + worker via stopDataCollection) and refreshes the
 * upload-status store so the UI reflects the new idle state.
 */
export const useStopDataCollection = (protocolId: TProtocolId | undefined, enabled: boolean = true) => {
    const [isStopping, setIsStopping] = useState(false);
    const isTauri = useTauriAvailability(enabled);
    const activeCompany = useNeurotuneStore((state) => state.activeCompany);
    const hasActiveCollection = useUploadStatusStore(
        (s) => s.dataCollectionStatus?.watcherRunning ?? false,
    );

    useEffect(() => {
        if (!enabled || !isTauri || !protocolId || !activeCompany) return;

        let timeoutId: number | null = null;
        let isMounted = true;

        const pollStatus = async () => {
            if (!isMounted) return;

            // Route through refresh() so the response actually populates
            // useUploadStatusStore.dataCollectionStatus (the bare IPC
            // helpers were no-ops with respect to the store).
            try {
                await useUploadStatusStore.getState().refresh(activeCompany.id, protocolId)
            } catch (error) {
                console.error("Error polling data acquisition status:", error);
            }

            if (isMounted) {
                timeoutId = window.setTimeout(pollStatus, 1000);
            }
        };

        pollStatus();

        return () => {
            isMounted = false;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [enabled, isTauri, protocolId, activeCompany]);

    /**
     * Stop data collection for this protocol. The Rust side tears
     * down both the watcher and the upload worker — there's no
     * separate "acquisitionId" anymore (the upload queue is a
     * global singleton).
     */
    const stopAcquisition = useCallback(async () => {
        if (!isTauri || !protocolId) return;

        setIsStopping(true);
        try {
            await tauriService.stopDataCollection();
            // Refresh the upload status store so the watcher pill flips
            // back to "Idle" / "Uploading only" immediately.
            await useUploadStatusStore.getState().refresh(activeCompany.id, protocolId);
        } catch (error) {
            showErrorNotification({
                title: "Data Collection Stop Failed",
                message: error instanceof Error ? error.message : String(error)
            });
        } finally {
            // Reset `isStopping` regardless of success or failure so
            // the button label flips back deterministically.
            setIsStopping(false);
        }
    }, [isTauri, protocolId, activeCompany]);

    return {
        isTauri,
        isStopping,
        hasActiveCollection,
        stopAcquisition,
    };
};